import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

/**
 * useState-Drop-In, der seinen Wert pro Session in sessionStorage spiegelt.
 *
 * Hintergrund: Listenansichten (Bestellungen, Kontrakte, Lieferungen, ...) sollen
 * Filter, Sortierung und Suche behalten, wenn der Nutzer auf eine Detailseite
 * navigiert und zurückkehrt. Da ein Detailaufruf dieselbe SPA-Session bleibt,
 * reicht sessionStorage – beim Tab-Schließen werden die Filter wieder verworfen
 * (kein "klebender" Filter über Tage hinweg).
 *
 * - `key` ist der eindeutige Storage-Schlüssel (z. B. `Kontraktuebersicht.kontraktNummer`).
 * - `defaultValue` greift, wenn nichts im Storage liegt (oder das Parsen fehlschlägt).
 * - Werte werden via JSON serialisiert, daher müssen sie serialisierbar sein.
 */
export function usePersistedState<T>(key: string, defaultValue: T): [T, Dispatch<SetStateAction<T>>, () => void] {
    const initial = useRef<T>(readFromStorage<T>(key, defaultValue));
    const [value, setValue] = useState<T>(initial.current);

    useEffect(() => {
        try {
            sessionStorage.setItem(key, JSON.stringify(value));
        } catch {
            // sessionStorage kann z.B. im Privacy-Modus disabled sein – stillschweigend ignorieren.
        }
    }, [key, value]);

    const reset = useCallback(() => {
        try {
            sessionStorage.removeItem(key);
        } catch {
            // siehe oben
        }
        setValue(defaultValue);
    }, [key, defaultValue]);

    return [value, setValue, reset];
}

function readFromStorage<T>(key: string, fallback: T): T {
    try {
        const raw = sessionStorage.getItem(key);
        if (raw === null) {
            return fallback;
        }
        return JSON.parse(raw) as T;
    } catch {
        return fallback;
    }
}

/**
 * Entfernt alle persistierten Listen-States (Filter/Sort/Suche/Scroll), z. B. beim Logout
 * oder beim expliziten "Filter zurücksetzen"-Button für einen Bereich.
 */
export function clearPersistedListState(prefix: string): void {
    try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const k = sessionStorage.key(i);
            if (k && k.startsWith(prefix)) {
                keysToRemove.push(k);
            }
        }
        keysToRemove.forEach((k) => sessionStorage.removeItem(k));
    } catch {
        // ignorieren
    }
}
